import {
  Activity,
  AlertTriangle,
  Bed,
  Building2,
  Button,
  Clock,
  Crown,
  Edit,
  Heart,
  Home,
  Input,
  Lock,
  Modal,
  Search,
  Select,
  Shield,
  Star,
  Stethoscope,
  Trash2,
  useDebounce,
  Users,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { StatCard } from "../../components/molecules/StatCard";
import { StatusBadge } from "../../components/molecules/StatusBadge/StatusBadge";
import { SubscriptionNotice } from "../../components/molecules/SubscriptionNotice";
import {
  EmergencyRoom,
  EmergencyRoomFormData,
  EmergencyRoomStatus,
  EmergencyRoomType,
  EmergencyStatistics,
} from "../../types/emergency.definition";
export const EmergencyManagement: React.FC = () => {
  const { t } = useTranslation();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [rooms, setRooms] = useState<EmergencyRoom[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<EmergencyRoom[]>([]);
  const [statistics, setStatistics] = useState<EmergencyStatistics | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<EmergencyRoom | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<EmergencyRoomStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<EmergencyRoomType[]>([]);

  // Debounce search term to improve performance
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [formData, setFormData] = useState<EmergencyRoomFormData>({
    name: "",
    roomNumber: "",
    type: "general",
    capacity: 1,
    equipment: [],
    assignedStaff: [],
  });

  // Mock data for demonstration
  const mockRooms: EmergencyRoom[] = useMemo(
    () => [
      {
        id: "1",
        name: "Trauma Room 1",
        roomNumber: "ER-001",
        status: "available",
        type: "trauma",
        capacity: 2,
        currentPatients: 0,
        equipment: [],
        assignedStaff: [],
        lastMaintenance: new Date("2024-01-15"),
        nextMaintenance: new Date("2024-02-15"),
        isActive: true,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-15"),
      },
      {
        id: "2",
        name: "Cardiac Emergency",
        roomNumber: "ER-002",
        status: "occupied",
        type: "cardiac",
        capacity: 1,
        currentPatients: 1,
        equipment: [],
        assignedStaff: [],
        lastMaintenance: new Date("2024-01-10"),
        nextMaintenance: new Date("2024-02-10"),
        isActive: true,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-10"),
      },
      {
        id: "3",
        name: "Pediatric ER",
        roomNumber: "ER-003",
        status: "maintenance",
        type: "pediatric",
        capacity: 3,
        currentPatients: 0,
        equipment: [],
        assignedStaff: [],
        lastMaintenance: new Date("2024-01-20"),
        nextMaintenance: new Date("2024-02-20"),
        isActive: true,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-20"),
      },
      {
        id: "4",
        name: "General Emergency",
        roomNumber: "ER-004",
        status: "available",
        type: "general",
        capacity: 4,
        currentPatients: 0,
        equipment: [],
        assignedStaff: [],
        lastMaintenance: new Date("2024-01-12"),
        nextMaintenance: new Date("2024-02-12"),
        isActive: true,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-12"),
      },
      {
        id: "5",
        name: "Isolation Room",
        roomNumber: "ER-005",
        status: "reserved",
        type: "isolation",
        capacity: 1,
        currentPatients: 0,
        equipment: [],
        assignedStaff: [],
        lastMaintenance: new Date("2024-01-18"),
        nextMaintenance: new Date("2024-02-18"),
        isActive: true,
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-18"),
      },
    ],
    [],
  );

  const mockStatistics: EmergencyStatistics = useMemo(
    () => ({
      totalRooms: 5,
      availableRooms: 2,
      occupiedRooms: 1,
      maintenanceRooms: 1,
      outOfServiceRooms: 0,
      totalEquipment: 25,
      operationalEquipment: 22,
      maintenanceEquipment: 3,
      totalStaff: 12,
      availableStaff: 8,
      averageResponseTime: 4.2,
      patientThroughput: 45,
      occupancyRate: 20,
      equipmentUtilization: 88,
    }),
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isSubscribed) {
      // Simulate API call
      const loadData = async () => {
        setIsLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setRooms(mockRooms);
        setStatistics(mockStatistics);
        setIsLoading(false);
      };

      loadData();
    }
  }, [isSubscribed, mockRooms, mockStatistics]);

  useEffect(() => {
    if (!isSubscribed) return;

    let filtered = rooms;

    // Search filter using debounced value
    if (debouncedSearchTerm) {
      filtered = filtered.filter(
        (room) =>
          room.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          room.roomNumber
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()),
      );
    }

    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((room) => statusFilter.includes(room.status));
    }

    // Type filter
    if (typeFilter.length > 0) {
      filtered = filtered.filter((room) => typeFilter.includes(room.type));
    }

    setFilteredRooms(filtered);
  }, [rooms, debouncedSearchTerm, statusFilter, typeFilter, isSubscribed]);

  const handleSubscribe = () => {
    setIsSubscribed(true);
  };

  const handleAddRoom = () => {
    setFormData({
      name: "",
      roomNumber: "",
      type: "general",
      capacity: 1,
      equipment: [],
      assignedStaff: [],
    });
    setShowAddModal(true);
  };

  const handleEditRoom = (room: EmergencyRoom) => {
    setSelectedRoom(room);
    setFormData({
      name: room.name,
      roomNumber: room.roomNumber,
      type: room.type,
      capacity: room.capacity,
      equipment: [],
      assignedStaff: [],
    });
    setShowEditModal(true);
  };

  const handleDeleteRoom = (room: EmergencyRoom) => {
    setSelectedRoom(room);
    setShowDeleteModal(true);
  };

  const handleReleaseRoom = (room: EmergencyRoom) => {
    setSelectedRoom(room);
    setShowReleaseModal(true);
  };

  const handleConfirmRelease = () => {
    if (selectedRoom) {
      setRooms((prev) =>
        prev.map((r) =>
          r.id === selectedRoom.id
            ? { ...r, status: "available" as EmergencyRoomStatus }
            : r,
        ),
      );
    }
    setShowReleaseModal(false);
  };

  const handleConfirmDelete = () => {
    if (selectedRoom) {
      setRooms((prev) => prev.filter((r) => r.id !== selectedRoom.id));
    }
    setShowDeleteModal(false);
  };

  const getStatusColor = (status: EmergencyRoomStatus) => {
    switch (status) {
      case "available":
        return "success";
      case "occupied":
        return "warning";
      case "maintenance":
        return "secondary";
      case "out_of_service":
        return "error";
      case "reserved":
        return "primary";
      default:
        return "neutral";
    }
  };

  const getTypeIcon = (type: EmergencyRoomType) => {
    switch (type) {
      case "trauma":
        return AlertTriangle;
      case "cardiac":
        return Heart;
      case "pediatric":
        return Users;
      case "general":
        return Home;
      case "isolation":
        return Shield;
      case "surgery":
        return Stethoscope;
      default:
        return Bed;
    }
  };

  const getTypeColor = (type: EmergencyRoomType) => {
    switch (type) {
      case "trauma":
        return "error";
      case "cardiac":
        return "primary";
      case "pediatric":
        return "success";
      case "general":
        return "neutral";
      case "isolation":
        return "warning";
      case "surgery":
        return "secondary";
      default:
        return "neutral";
    }
  };

  if (!isSubscribed) {
    return (
      <div className="p-6 space-y-6 animate-fadeIn">
        {/* Header */}
        <div
          className={`transition-all duration-700 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
            }`}
        >
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex justify-center items-center w-12 h-12 bg-red-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-neutral-900">
                  {t("emergency.title")}
                </h1>
                <p className="text-neutral-600">{t("emergency.subtitle")}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleSubscribe}
              className="hover-scale"
            >
              <Crown className="mr-2 w-4 h-4" />
              {t("emergency.simulateSubscription")}
            </Button>
          </div>
        </div>

        {/* Subscription Notice */}
        <SubscriptionNotice
          title={t("emergency.subscription.title")}
          description={t("emergency.subscription.description")}
          feature={t("emergency.subscription.feature")}
          onSubscribe={handleSubscribe}
          onContact={() => {
            // Contact sales functionality to be implemented
          }}
        />

        {/* Feature Preview */}
        <div
          className={`bg-white rounded-xl border shadow-soft border-neutral-200 p-6 transition-all duration-700 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
            }`}
          style={{ transitionDelay: "400ms" }}
        >
          <div className="text-center">
            <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl">
              <Star className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-neutral-900">
              {t("emergency.subscription.comingSoon")}
            </h2>
            <p className="mb-6 text-neutral-600">
              {t("emergency.subscription.unlockFeatures")}
            </p>
            <div className="flex justify-center space-x-4">
              <div className="relative">
                <Button
                  disabled
                  className="relative text-white bg-gradient-to-r from-red-400 to-orange-400 opacity-60 cursor-not-allowed"
                >
                  <Crown className="mr-2 w-4 h-4" />
                  {t("emergency.subscription.subscribeNow")}
                </Button>
                <span className="absolute -top-2 -right-2 px-2 py-1 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-lg animate-pulse">
                  {t("emergency.subscription.comingSoonTag")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 rounded-full border-2 animate-spin border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div
        className={`transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex justify-center items-center w-12 h-12 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                {t("emergency.title")}
              </h1>
              <p className="text-neutral-600">{t("emergency.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={() => setIsSubscribed(false)}
              className="hover-scale"
            >
              <Lock className="mr-2 w-4 h-4" />
              {t("emergency.unsubscribe")}
            </Button>
            <Button onClick={handleAddRoom} className="hover-scale">
              <Crown className="mr-2 w-4 h-4" />
              {t("emergency.addRoom")}
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div
          className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 transition-all duration-700 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
            }`}
          style={{ transitionDelay: "200ms" }}
        >
          <StatCard
            title={t("emergency.stats.totalRooms")}
            value={statistics.totalRooms.toString()}
            icon={Building2}
            change={{
              value: `${statistics.availableRooms} ${t(
                "emergency.stats.available",
              )}`,
              type: "neutral",
            }}
          />
          <StatCard
            title={t("emergency.stats.occupancyRate")}
            value={`${statistics.occupancyRate}%`}
            icon={Bed}
            change={{
              value: `${statistics.occupiedRooms} ${t(
                "emergency.stats.occupied",
              )}`,
              type: "neutral",
            }}
          />
          <StatCard
            title={t("emergency.stats.equipmentUtilization")}
            value={`${statistics.equipmentUtilization}%`}
            icon={Activity}
            change={{
              value: `${statistics.operationalEquipment}/${statistics.totalEquipment}`,
              type: "neutral",
            }}
          />
          <StatCard
            title={t("emergency.stats.responseTime")}
            value={`${statistics.averageResponseTime}m`}
            icon={Clock}
            change={{ value: t("emergency.stats.average"), type: "neutral" }}
          />
        </div>
      )}

      {/* Filters and Search */}
      <div
        className={`bg-white rounded-xl border shadow-soft border-neutral-200 p-4 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "400ms" }}
      >
        <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-neutral-400" />
              <Input
                label={t("emergency.searchRooms")}
                placeholder={t("emergency.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {searchTerm !== debouncedSearchTerm &&
                searchTerm.trim().length > 0 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
                  </div>
                )}
            </div>
            {searchTerm !== debouncedSearchTerm &&
              searchTerm.trim().length > 0 && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t("emergency.searching")}
                </p>
              )}
          </div>
          <div className="flex items-center space-x-3">
            <Select
              label={t("emergency.status.label")}
              value={statusFilter.join(",")}
              onChange={(value) =>
                setStatusFilter(
                  value
                    ? ((value as string).split(",") as EmergencyRoomStatus[])
                    : [],
                )
              }
              options={[
                { value: "", label: t("emergency.allStatus") },
                { value: "available", label: t("emergency.status.available") },
                { value: "occupied", label: t("emergency.status.occupied") },
                {
                  value: "maintenance",
                  label: t("emergency.status.maintenance"),
                },
                {
                  value: "out_of_service",
                  label: t("emergency.status.outOfService"),
                },
                { value: "reserved", label: t("emergency.status.reserved") },
              ]}
            />
            <Select
              label={t("emergency.type.label")}
              value={typeFilter.join(",")}
              onChange={(value) =>
                setTypeFilter(
                  value
                    ? ((value as string).split(",") as EmergencyRoomType[])
                    : [],
                )
              }
              options={[
                { value: "", label: t("emergency.allTypes") },
                { value: "trauma", label: t("emergency.type.trauma") },
                { value: "cardiac", label: t("emergency.type.cardiac") },
                { value: "pediatric", label: t("emergency.type.pediatric") },
                { value: "general", label: t("emergency.type.general") },
                { value: "isolation", label: t("emergency.type.isolation") },
                { value: "surgery", label: t("emergency.type.surgery") },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Rooms Grid */}
      <div
        className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "600ms" }}
      >
        {filteredRooms.map((room, index) => {
          const TypeIcon = getTypeIcon(room.type);
          return (
            <div
              key={room.id}
              className={`bg-white rounded-xl border shadow-soft border-neutral-200 p-6 transition-all duration-300 hover:shadow-lg hover-lift ${animateCards
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
                }`}
              style={{ transitionDelay: `${(index + 8) * 100}ms` }}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className={`flex justify-center items-center w-10 h-10 rounded-lg bg-${getTypeColor(
                      room.type,
                    )}-100`}
                  >
                    <TypeIcon
                      className={`w-5 h-5 text-${getTypeColor(room.type)}-600`}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900">
                      {room.name}
                    </h3>
                    <p className="text-sm text-neutral-500">
                      {room.roomNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <StatusBadge
                    status={room.status}
                    variant={getStatusColor(room.status)}
                  />
                </div>
              </div>

              <div className="mb-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600">
                    {t("emergency.room.capacity")}
                  </span>
                  <span className="text-sm font-medium text-neutral-900">
                    {room.currentPatients}/{room.capacity}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600">
                    {t("emergency.room.type")}
                  </span>
                  <span className="text-sm font-medium capitalize text-neutral-900">
                    {t(`emergency.type.${room.type}`)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600">
                    {t("emergency.room.lastMaintenance")}
                  </span>
                  <span className="text-sm font-medium text-neutral-900">
                    {room.lastMaintenance.toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-neutral-100">
                <div className="flex items-center space-x-2">
                  {room.status === "occupied" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReleaseRoom(room)}
                      className="text-success-600 border-success-200 hover:bg-success-50"
                    >
                      {t("emergency.room.release")}
                    </Button>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditRoom(room)}
                    className="text-primary-600 hover:bg-ews-primary-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRoom(room)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Room Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t("emergency.modal.add.title")}
        variant="info"
        size="2xl"
      >
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label={t("emergency.modal.add.roomName")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={t("emergency.modal.add.roomNamePlaceholder")}
              />

              <Input
                label={t("emergency.modal.add.roomNumber")}
                value={formData.roomNumber}
                onChange={(e) =>
                  setFormData({ ...formData, roomNumber: e.target.value })
                }
                placeholder={t("emergency.modal.add.roomNumberPlaceholder")}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label={t("emergency.modal.add.roomType")}
                value={formData.type}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    type: value as EmergencyRoomType,
                  })
                }
                options={[
                  { value: "general", label: t("emergency.type.general") },
                  { value: "trauma", label: t("emergency.type.trauma") },
                  { value: "cardiac", label: t("emergency.type.cardiac") },
                  { value: "pediatric", label: t("emergency.type.pediatric") },
                  { value: "isolation", label: t("emergency.type.isolation") },
                  { value: "surgery", label: t("emergency.type.surgery") },
                ]}
              />
              <div>
                <Input
                  label={t("emergency.modal.add.capacity")}
                  type="number"
                  value={formData.capacity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      capacity: parseInt(e.target.value),
                    })
                  }
                  min="1"
                  max="10"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6 space-x-3">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              {t("emergency.modal.add.cancel")}
            </Button>
            <Button
              onClick={() => {
                // Handle add room logic
                setShowAddModal(false);
              }}
            >
              {t("emergency.modal.add.submit")}
            </Button>
          </div>
      </Modal>

      {/* Edit Room Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t("emergency.modal.edit.title")}
        variant="info"
        size="2xl"
      >
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label={t("emergency.modal.add.roomName")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={t("emergency.modal.add.roomNamePlaceholder")}
              />

              <Input
                label={t("emergency.modal.add.roomNumber")}
                value={formData.roomNumber}
                onChange={(e) =>
                  setFormData({ ...formData, roomNumber: e.target.value })
                }
                placeholder={t("emergency.modal.add.roomNumberPlaceholder")}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label={t("emergency.modal.add.roomType")}
                value={formData.type}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    type: value as EmergencyRoomType,
                  })
                }
                options={[
                  { value: "general", label: t("emergency.type.general") },
                  { value: "trauma", label: t("emergency.type.trauma") },
                  { value: "cardiac", label: t("emergency.type.cardiac") },
                  { value: "pediatric", label: t("emergency.type.pediatric") },
                  { value: "isolation", label: t("emergency.type.isolation") },
                  { value: "surgery", label: t("emergency.type.surgery") },
                ]}
              />
              <div>
                <Input
                  label={t("emergency.modal.add.capacity")}
                  type="number"
                  value={formData.capacity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      capacity: parseInt(e.target.value),
                    })
                  }
                  min="1"
                  max="10"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6 space-x-3">
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>
              {t("emergency.modal.add.cancel")}
            </Button>
            <Button
              onClick={() => {
                // Handle edit room logic
                setShowEditModal(false);
              }}
            >
              {t("emergency.modal.edit.submit")}
            </Button>
          </div>
      </Modal>

      {/* Delete Room Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title={t("emergency.modal.delete.title")}
        message={t("emergency.modal.delete.message", {
          name: selectedRoom?.name || "",
        })}
        confirmLabel={t("emergency.modal.delete.confirm")}
        cancelLabel={t("emergency.modal.delete.cancel")}
        variant="danger"
      />

      {/* Release Room Modal */}
      <ConfirmationModal
        isOpen={showReleaseModal}
        onCancel={() => setShowReleaseModal(false)}
        onConfirm={handleConfirmRelease}
        title={t("emergency.modal.release.title")}
        message={t("emergency.modal.release.message", {
          name: selectedRoom?.name || "",
        })}
        confirmLabel={t("emergency.modal.release.confirm")}
        cancelLabel={t("emergency.modal.release.cancel")}
        variant="warning"
      />
    </div>
  );
};
